import React, { useState, useEffect } from "react";
import {
  Package,
  Truck,
  Clock,
  CheckCircle,
  XCircle,
  Calendar,
  Filter,
  Plus,
  RefreshCw,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { FaChevronRight } from "react-icons/fa";
import { useCurrency } from "../../context/CurrencyContext";
import { formatingPrice } from "../../utils/formatingPrice";
import { useTranslation } from "react-i18next";

const UserOrders = () => {
  const { currency, convertPrice } = useCurrency();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language || "en";

  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("All");
  const navigate = useNavigate();

  // Helper function to get localized text
  const getLocalizedText = (item, field) => {
    if (!item || !item[field]) return "";
    
    // If value is a string (backward compatibility)
    if (typeof item[field] === 'string') {
      return item[field];
    }
    
    // If value is an object with language keys
    if (typeof item[field] === 'object') {
      return item[field][currentLanguage] || 
             item[field].en || 
             item[field].hi || 
             item[field].es || 
             item[field].zh || 
             "";
    }
    
    return "";
  };

  const getUserData = () => {
    try {
      const userData = localStorage?.getItem("pm-user-data");
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error("Error parsing user data:", error);
      return null;
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const user = getUserData();

      if (!user) {
        setError(t("orders.user_not_found") || "User not found");
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/orders/get-user-orders/${user._id}`
      );

      if (!response.ok) {
        throw new Error(t("orders.fetch_failed") || "Failed to fetch orders");
      }

      const data = await response.json();
      
      // Add localized product names to each order
      const ordersWithLocalizedNames = data.map(order => ({
        ...order,
        products: order.products.map(item => ({
          ...item,
          product: {
            ...item.product,
            localizedName: getLocalizedText(item.product, "name")
          }
        }))
      }));
      
      setOrders(ordersWithLocalizedNames);
      setFilteredOrders(ordersWithLocalizedNames);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching orders:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // Update localized names when language changes
  useEffect(() => {
    if (orders.length > 0) {
      const updatedOrders = orders.map(order => ({
        ...order,
        products: order.products.map(item => ({
          ...item,
          product: {
            ...item.product,
            localizedName: getLocalizedText(item.product, "name")
          }
        }))
      }));
      
      setOrders(updatedOrders);
      
      // Refilter with updated orders
      if (selectedStatus === "All") {
        setFilteredOrders(updatedOrders);
      } else {
        setFilteredOrders(
          updatedOrders.filter(
            (order) => order.status.toLowerCase() === selectedStatus.toLowerCase()
          )
        );
      }
    }
  }, [currentLanguage]);

  // Filter orders by status
  useEffect(() => {
    if (selectedStatus === "All") {
      setFilteredOrders(orders);
    } else {
      setFilteredOrders(
        orders.filter(
          (order) => order.status.toLowerCase() === selectedStatus.toLowerCase()
        )
      );
    }
  }, [selectedStatus, orders]);

  // Calculate order total manually (same as Cart component)
  const calculateOrderTotal = (order) => {
    if (!order || !order.products) return 0;

    const subtotal = order.products.reduce(
      (sum, item) => sum + (item.product?.price || 0) * (item.quantity || 1),
      0
    );

    const shipping = order.products.reduce(
      (sum, item) =>
        sum + (item.product?.shippingCharge || 0) * (item.quantity || 1),
      0
    );

    const gst = order.products.reduce(
      (sum, item) =>
        sum +
        ((item.product?.price || 0) *
          (item.quantity || 1) *
          (item.product?.gstPercentage || 0)) /
          100,
      0
    );

    const extraTax = order.products.reduce(
      (sum, item) =>
        sum +
        ((item.product?.price || 0) *
          (item.quantity || 1) *
          (item.product?.extraTaxPercentage || 0)) /
          100,
      0
    );

    return subtotal + shipping + gst + extraTax;
  };

  // Get unique statuses for filter
  const getOrderStatuses = () => {
    const statuses = ["All", ...new Set(orders.map((order) => order.status))];
    return statuses;
  };

  // Get status counts
  const getStatusCounts = () => {
    const counts = {};
    orders.forEach((order) => {
      counts[order.status] = (counts[order.status] || 0) + 1;
    });
    return counts;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "text-yellow-600 bg-yellow-100 border-yellow-200";
      case "shipped":
        return "text-purple-600 bg-purple-100 border-purple-200";
      case "delivered":
        return "text-green-600 bg-green-100 border-green-200";
      case "cancelled":
        return "text-red-600 bg-red-100 border-red-200";
      case "processing":
        return "text-blue-600 bg-blue-100 border-blue-200";
      default:
        return "text-gray-600 bg-gray-100 border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return <Clock className="w-4 h-4" />;
      case "shipped":
        return <Truck className="w-4 h-4" />;
      case "delivered":
        return <CheckCircle className="w-4 h-4" />;
      case "cancelled":
        return <XCircle className="w-4 h-4" />;
      case "processing":
        return <RefreshCw className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusDisplayText = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return t("orders.status_pending") || "Pending";
      case "shipped":
        return t("orders.status_shipped") || "Shipped";
      case "delivered":
        return t("orders.status_delivered") || "Delivered";
      case "cancelled":
        return t("orders.status_cancelled") || "Cancelled";
      case "processing":
        return t("orders.status_processing") || "Processing";
      default:
        return status || t("orders.status_unknown") || "Unknown";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBFBFB]">
        <div className="w-full mx-auto px-4 md:px-10 py-8">
          <div className="animate-pulse">
            {/* Header Skeleton */}
            <div className="h-8 bg-gray-300 rounded w-48 mb-6"></div>
            
            {/* Filter Skeleton */}
            <div className="mb-8">
              <div className="h-6 bg-gray-300 rounded w-32 mb-4"></div>
              <div className="flex flex-wrap gap-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-10 bg-gray-300 rounded w-24"></div>
                ))}
              </div>
            </div>

            {/* Order Cards Skeleton */}
            <div className="space-y-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                  <div className="h-6 bg-gray-300 rounded w-32 mb-4"></div>
                  <div className="flex items-start space-x-4">
                    <div className="w-20 h-20 bg-gray-300 rounded-lg"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-300 rounded w-full"></div>
                      <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                      <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FBFBFB] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {t("orders.error_loading") || "Error Loading Orders"}
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchOrders}
            className="bg-[#204470] text-white px-6 py-3 rounded-lg hover:bg-[#153b68] transition-colors font-medium"
          >
            <RefreshCw className="w-4 h-4 inline mr-2" />
            {t("orders.try_again") || "Try Again"}
          </button>
        </div>
      </div>
    );
  }

  const statusCounts = getStatusCounts();

  return (
    <div className="min-h-screen bg-[#FBFBFB]">
      <div className="w-full mx-auto px-4 md:px-10 py-4 md:pb-10">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-philosopher text-gray-900">
                {t("orders.my_orders") || "My Orders"}
              </h1>
              <div className="flex items-center text-gray-500 mt-1 text-sm">
                <span
                  onClick={() => navigate("/")}
                  className="cursor-pointer hover:underline hover:text-gray-700"
                >
                  {t("orders.home") || "Home"}
                </span>
                <FaChevronRight className="mx-2 text-xs" />
                <span className="text-gray-900 font-medium">
                  {t("orders.my_orders") || "My Orders"}
                </span>
              </div>
            </div>
            {!loading && (
              <span className="bg-gray-100 text-gray-800 px-4 py-2 rounded-full text-sm font-medium">
                {orders.length} {orders.length === 1 
                  ? t("orders.order_singular") || "Order" 
                  : t("orders.order_plural") || "Orders"}
              </span>
            )}
          </div>
        </div>

        {/* Status Filter */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-4">
            <Filter className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">
              {t("orders.filter_by_status") || "Filter by Status"}
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            {getOrderStatuses().map((status) => (
              <button
                key={status}
                onClick={() => setSelectedStatus(status)}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  selectedStatus === status
                    ? "bg-[#204470] text-white shadow-sm"
                    : "bg-white text-gray-700 hover:bg-gray-50 hover:shadow-sm"
                } border`}
              >
                <span className="flex items-center">
                  {status !== "All" && getStatusIcon(status)}
                  <span className={status !== "All" ? "ml-1" : ""}>
                    {status === "All" 
                      ? t("orders.filter_all") || "All" 
                      : getStatusDisplayText(status)}
                  </span>
                </span>
                <span className="ml-2 text-sm opacity-80">
                  {status === "All" 
                    ? `(${orders.length})`
                    : statusCounts[status] ? `(${statusCounts[status]})` : "(0)"}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* No Orders Message */}
        {filteredOrders.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="w-10 h-10 text-gray-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {selectedStatus === "All"
                ? t("orders.no_orders_found") || "No Orders Found"
                : `${t("orders.no_status_orders") || "No"} ${getStatusDisplayText(selectedStatus)} ${t("orders.orders") || "Orders"}`}
            </h2>
            <p className="text-gray-600 mb-6">
              {selectedStatus === "All"
                ? t("orders.no_orders_message") || "You haven't placed any orders yet."
                : t("orders.no_status_orders_message") || `You have no ${selectedStatus.toLowerCase()} orders.`}
            </p>
            <button
              onClick={() => navigate("/allproduct")}
              className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg transition-colors font-medium"
            >
              {t("orders.browse_products") || "Browse Products"}
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredOrders.map((order) => {
              const calculatedTotal = calculateOrderTotal(order);
              const firstProduct = order.products[0];
              const additionalProductsCount = order.products.length - 1;
              const productName = firstProduct?.product?.localizedName || 
                                 getLocalizedText(firstProduct?.product, "name") || 
                                 t("orders.untitled_product") || "Untitled Product";
              const productImage = firstProduct?.product?.images?.[0] || "https://via.placeholder.com/100x100?text=Product";

              return (
                <div
                  onClick={() => navigate(`/order-details/${order._id}`)}
                  key={order._id}
                  className="bg-white cursor-pointer rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-300"
                >
                  {/* Order Header */}
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <div className="flex justify-between items-start flex-wrap gap-2">
                      {/* Left side */}
                      <div>
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                          {t("orders.order_number") || "Order"} #{order._id.slice(-8)}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-600 flex items-center mt-1">
                          <Calendar className="w-4 h-4 mr-1" />
                          {formatDate(order.createdAt)}
                        </p>
                      </div>

                      {/* Right side status + amount */}
                      <div className="flex flex-col items-end">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm font-medium border ${getStatusColor(
                            order.status
                          )}`}
                        >
                          {getStatusIcon(order.status)}
                          <span className="ml-1">{getStatusDisplayText(order.status)}</span>
                        </span>

                        <p className="text-sm sm:text-base font-bold text-gray-900 mt-2">
                          {formatingPrice(
                            convertPrice(calculatedTotal),
                            currency
                          )}
                        </p>
                        <p className="text-xs text-gray-500">{t("orders.total") || "Total"}</p>
                      </div>
                    </div>
                  </div>

                  {/* Product Section */}
                  <div className="p-4">
                    <div className="flex items-start space-x-4 p-3 bg-gray-50 rounded-lg">
                      {/* Product Image */}
                      <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0">
                        <img
                          src={productImage}
                          alt={productName}
                          className="w-full h-full object-cover rounded-lg"
                          onError={(e) => {
                            e.target.src = "https://via.placeholder.com/100x100?text=Product";
                          }}
                        />
                      </div>

                      {/* Product Info */}
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 text-sm sm:text-base line-clamp-2">
                          {productName}
                        </h4>

                        <div className="text-xs sm:text-sm text-gray-600 mt-1">
                          {t("orders.quantity") || "Qty"}: {firstProduct?.quantity || 1} •{" "}
                          {formatingPrice(
                            convertPrice(firstProduct?.product?.price || 0),
                            currency
                          )}
                        </div>

                        {/* Warranty Info */}
                        {firstProduct?.product?.warranty?.hasWarranty && (
                          <span className="inline-flex items-center px-2 py-1 mt-2 rounded-full text-[10px] sm:text-xs bg-green-100 text-green-800 border border-green-200">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {firstProduct?.product?.warranty?.warrantyDays} {t("orders.warranty_days") || "Days Warranty"}
                          </span>
                        )}

                        {/* Exchange Request Indicator */}
                        {order.exchangeRequest?.isRequested && (
                          <span className="inline-flex items-center px-2 py-1 mt-2 ml-2 rounded-full text-[10px] sm:text-xs bg-blue-100 text-blue-800 border border-blue-200">
                            <RefreshCw className="w-3 h-3 mr-1" />
                            {t("orders.exchange_requested") || "Exchange Requested"}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Additional Products */}
                    {additionalProductsCount > 0 && (
                      <div className="mt-3 px-3 py-2 bg-blue-50 rounded-lg border border-blue-200 flex justify-between items-center">
                        <div className="flex items-center text-xs sm:text-sm text-blue-900">
                          <Plus className="w-4 h-4 text-blue-600 mr-1" />
                          {additionalProductsCount} {t("orders.more_products") || "more product"}
                          {additionalProductsCount > 1 ? "s" : ""}
                        </div>

                        <button
                          className="text-blue-600 hover:text-blue-800 text-xs sm:text-sm font-medium"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/order-details/${order._id}`);
                          }}
                        >
                          {t("orders.view") || "View"}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Order Summary Footer */}
                  <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">
                        {order.products.length} {order.products.length === 1 
                          ? t("orders.item_singular") || "item" 
                          : t("orders.item_plural") || "items"}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/order-details/${order._id}`);
                        }}
                        className="text-[#204470] hover:text-[#153b68] font-medium"
                      >
                        {t("orders.view_details") || "View Details"} →
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserOrders;




// import React, { useState, useEffect } from "react";
// import {
//   Package,
//   Truck,
//   Clock,
//   CheckCircle,
//   XCircle,
//   Calendar,
//   Filter,
//   Plus,
// } from "lucide-react";

// import { useNavigate } from "react-router-dom";

// import { FaChevronRight } from "react-icons/fa";
// import { useCurrency } from "../../context/CurrencyContext";
// import { formatingPrice } from "../../utils/formatingPrice";

// const UserOrders = () => {
//   const { currency, convertPrice } = useCurrency();

//   const [orders, setOrders] = useState([]);
//   const [filteredOrders, setFilteredOrders] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [selectedStatus, setSelectedStatus] = useState("All");
//   const navigate = useNavigate();
//   const getUserData = () => {
//     try {
//       const userData = localStorage?.getItem("pm-user-data");
//       return userData ? JSON.parse(userData) : null;
//     } catch (error) {
//       console.error("Error parsing user data:", error);
//       return null;
//     }
//   };

//   const fetchOrders = async () => {
//     try {
//       setLoading(true);
//       const user = getUserData();

//       if (!user) {
//         setError("User not found");
//         setLoading(false);
//         return;
//       }

//       const response = await fetch(
//         `${process.env.REACT_APP_API_URL}/orders/get-user-orders/${user._id}`
//       );

//       if (!response.ok) {
//         throw new Error("Failed to fetch orders");
//       }

//       const data = await response.json();
//       setOrders(data);
//       setFilteredOrders(data);
//       setError(null);
//     } catch (err) {
//       setError(err.message);
//       console.error("Error fetching orders:", err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchOrders();
//   }, []);

//   // Filter orders by status
//   useEffect(() => {
//     if (selectedStatus === "All") {
//       setFilteredOrders(orders);
//     } else {
//       setFilteredOrders(
//         orders.filter(
//           (order) => order.status.toLowerCase() === selectedStatus.toLowerCase()
//         )
//       );
//     }
//   }, [selectedStatus, orders]);

//   // Calculate order total manually (same as Cart component)
//   const calculateOrderTotal = (order) => {
//     if (!order || !order.products) return 0;

//     const subtotal = order.products.reduce(
//       (sum, item) => sum + (item.product?.price || 0) * (item.quantity || 1),
//       0
//     );

//     const shipping = order.products.reduce(
//       (sum, item) =>
//         sum + (item.product?.shippingCharge || 0) * (item.quantity || 1),
//       0
//     );

//     const gst = order.products.reduce(
//       (sum, item) =>
//         sum +
//         ((item.product?.price || 0) *
//           (item.quantity || 1) *
//           (item.product?.gstPercentage || 0)) /
//           100,
//       0
//     );

//     const extraTax = order.products.reduce(
//       (sum, item) =>
//         sum +
//         ((item.product?.price || 0) *
//           (item.quantity || 1) *
//           (item.product?.extraTaxPercentage || 0)) /
//           100,
//       0
//     );

//     return subtotal + shipping + gst + extraTax;
//   };

//   // Get unique statuses for filter
//   const getOrderStatuses = () => {
//     const statuses = ["All", ...new Set(orders.map((order) => order.status))];
//     return statuses;
//   };

//   // Get status counts
//   const getStatusCounts = () => {
//     const counts = {};
//     orders.forEach((order) => {
//       counts[order.status] = (counts[order.status] || 0) + 1;
//     });
//     return counts;
//   };

//   const getStatusColor = (status) => {
//     switch (status?.toLowerCase()) {
//       case "pending":
//         return "text-yellow-600 bg-yellow-100";
//       case "shipped":
//         return "text-purple-600 bg-purple-100";
//       case "delivered":
//         return "text-green-600 bg-green-100";
//       case "cancelled":
//         return "text-red-600 bg-red-100";
//       default:
//         return "text-gray-600 bg-gray-100";
//     }
//   };

//   const getStatusIcon = (status) => {
//     switch (status?.toLowerCase()) {
//       case "pending":
//         return <Clock className="w-4 h-4" />;
//       case "shipped":
//         return <Truck className="w-4 h-4" />;
//       case "delivered":
//         return <CheckCircle className="w-4 h-4" />;
//       case "cancelled":
//         return <XCircle className="w-4 h-4" />;
//       default:
//         return <Clock className="w-4 h-4" />;
//     }
//   };

//   const formatDate = (dateString) => {
//     return new Date(dateString).toLocaleDateString("en-IN", {
//       year: "numeric",
//       month: "short",
//       day: "numeric",
//       hour: "2-digit",
//       minute: "2-digit",
//     });
//   };

//   const formatCurrency = (amount) => {
//     return new Intl.NumberFormat("en-IN", {
//       style: "currency",
//       currency: "INR",
//     }).format(amount);
//   };

//   if (loading) {
//     return (
//       <div className="min-h-screen bg-gray-50">
//         <div className="w-full mx-auto px-4 py-8">
//           <div className="animate-pulse">
//             <div className="h-8 bg-gray-300 rounded w-48 mb-6"></div>
//             <div className="space-y-6">
//               {[1, 2, 3].map((i) => (
//                 <div key={i} className="bg-white rounded-lg p-6 shadow-sm">
//                   <div className="h-6 bg-gray-300 rounded w-32 mb-4"></div>
//                   <div className="h-4 bg-gray-300 rounded w-full mb-2"></div>
//                   <div className="h-4 bg-gray-300 rounded w-3/4"></div>
//                 </div>
//               ))}
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="min-h-screen bg-gray-50 flex items-center justify-center">
//         <div className="text-center">
//           <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
//           <h2 className="text-xl font-semibold text-gray-900 mb-2">
//             Error Loading Orders
//           </h2>
//           <p className="text-gray-600 mb-4">{error}</p>
//           <button
//             onClick={fetchOrders}
//             className="bg-[#204470] text-white px-6 py-2 rounded-lg hover:bg-[#153b68] transition-colors"
//           >
//             Try Again
//           </button>
//         </div>
//       </div>
//     );
//   }

//   const statusCounts = getStatusCounts();

//   return (
//     <div className="min-h-screen bg-[#FBFBFB]">
//       <div className="w-full mx-auto px-4 md:px-10 py-4 md:pb-10">
//         <div className="mb-8">
//           <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
//             <div>
//               <h1 className="text-3xl font-philosopher text-gray-900">
//                 My Orders
//               </h1>
//               <div className="flex items-center text-gray-500 mt-1 text-sm">
//                 <span
//                   onClick={() => navigate("/")}
//                   className="cursor-pointer hover:underline"
//                 >
//                   Home
//                 </span>
//                 <FaChevronRight className="mx-2 text-xs" />
//                 <span className="text-gray-900 font-medium">My Orders</span>
//               </div>
//             </div>
//             {!loading && (
//               <span className="bg-gray-100 text-gray-800 px-4 py-2 rounded-full text-sm font-medium">
//                 {orders.length} {orders.length === 1 ? "Order" : "Orders"}
//               </span>
//             )}
//           </div>
//         </div>

//         {/* Status Filter */}
//         <div className="mb-8">
//           <div className="flex items-center space-x-2 mb-4">
//             <Filter className="w-5 h-5 text-gray-500" />
//             <h2 className="text-lg font-semibold text-gray-900">
//               Filter by Status
//             </h2>
//           </div>
//           <div className="flex flex-wrap gap-3">
//             {getOrderStatuses().map((status) => (
//               <button
//                 key={status}
//                 onClick={() => setSelectedStatus(status)}
//                 className={`px-4 py-2 rounded-lg font-medium transition-colors ${
//                   selectedStatus === status
//                     ? "bg-[#204470] text-white"
//                     : "bg-white text-gray-700 hover:bg-gray-50"
//                 } border border-gray-200`}
//               >
//                 {status}
//                 {status !== "All" && statusCounts[status] && (
//                   <span className="ml-2 text-sm">({statusCounts[status]})</span>
//                 )}
//                 {status === "All" && (
//                   <span className="ml-2 text-sm">({orders.length})</span>
//                 )}
//               </button>
//             ))}
//           </div>
//         </div>

//         {filteredOrders.length === 0 ? (
//           <div className="text-center py-12">
//             <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
//             <h2 className="text-xl font-semibold text-gray-900 mb-2">
//               {selectedStatus === "All"
//                 ? "No Orders Found"
//                 : `No ${selectedStatus} Orders`}
//             </h2>
//             <p className="text-gray-600">
//               {selectedStatus === "All"
//                 ? "You haven't placed any orders yet."
//                 : `You have no ${selectedStatus.toLowerCase()} orders.`}
//             </p>
//           </div>
//         ) : (
//           <div className="space-y-6">
//             {filteredOrders.map((order) => {
//               const calculatedTotal = calculateOrderTotal(order);
//               const firstProduct = order.products[0];
//               const additionalProductsCount = order.products.length - 1;

//               return (
//                 <div
//                   onClick={() => navigate(`/order-details/${order._id}`)}
//                   key={order._id}
//                   className="bg-white cursor-pointer rounded-lg shadow-sm border border-gray-200 overflow-hidden"
//                 >
//                   {/* Order Header */}
//                   <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
//                     <div className="flex justify-between items-start flex-wrap gap-2">
//                       {/* Left side */}
//                       <div>
//                         <h3 className="text-base sm:text-lg font-semibold text-gray-900">
//                           Order #{order._id.slice(-8)}
//                         </h3>
//                         <p className="text-xs sm:text-sm text-gray-600 flex items-center mt-1">
//                           <Calendar className="w-4 h-4 mr-1" />
//                           {formatDate(order.createdAt)}
//                         </p>
//                       </div>

//                       {/* Right side status + amount */}
//                       <div className="flex flex-col items-end">
//                         <span
//                           className={`inline-flex items-center px-2 py-1 rounded-full text-xs sm:text-sm font-medium ${getStatusColor(
//                             order.status
//                           )}`}
//                         >
//                           {getStatusIcon(order.status)}
//                           <span className="ml-1">{order.status}</span>
//                         </span>

//                         <p className="text-sm sm:text-base font-bold text-gray-900 mt-1">
//                           {formatingPrice(
//                             convertPrice(formatCurrency(calculatedTotal)),
//                             currency
//                           )}

//                           {/* {formatCurrency(calculatedTotal)} */}
//                         </p>
//                         <p className="text-xs text-gray-500">Total</p>
//                       </div>
//                     </div>
//                   </div>

//                   {/* Product Section */}
//                   <div className="p-4">
//                     <div className="flex items-start space-x-4 p-3 bg-gray-50 rounded-lg">
//                       {/* Product Image */}
//                       <img
//                         src={
//                           firstProduct?.product?.images?.[0] ||
//                           "/api/placeholder/80/80"
//                         }
//                         alt={firstProduct?.product?.name || "Product"}
//                         className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-lg flex-shrink-0"
//                         onError={(e) =>
//                           (e.target.src = "/api/placeholder/80/80")
//                         }
//                       />

//                       {/* Product Info */}
//                       <div className="flex-1">
//                         <h4 className="font-semibold text-gray-900 text-sm sm:text-base line-clamp-2">
//                           {firstProduct?.product?.name || "Unknown Product"}
//                         </h4>

//                         <div className="text-xs sm:text-sm text-gray-600 mt-1">
//                           Qty: {firstProduct?.quantity || 1} •{" "}
//                           {formatingPrice(
//                             convertPrice(
//                               formatCurrency(firstProduct?.product?.price || 0)
//                             ),
//                             currency
//                           )}
//                           {/* {formatCurrency(firstProduct?.product?.price || 0)} */}
//                         </div>

//                         {firstProduct?.product?.warranty?.hasWarranty && (
//                           <span className="inline-flex items-center px-2 py-1 mt-2 rounded-full text-[10px] sm:text-xs bg-green-100 text-green-800">
//                             <CheckCircle className="w-3 h-3 mr-1" />
//                             {firstProduct?.product?.warranty?.warrantyDays} Days
//                             Warranty
//                           </span>
//                         )}
//                       </div>
//                     </div>

//                     {/* Additional Products */}
//                     {additionalProductsCount > 0 && (
//                       <div className="mt-3 px-3 py-2 bg-blue-50 rounded-lg border border-blue-200 flex justify-between items-center">
//                         <div className="flex items-center text-xs sm:text-sm text-blue-900">
//                           <Plus className="w-4 h-4 text-blue-600 mr-1" />
//                           {additionalProductsCount} more product
//                           {additionalProductsCount > 1 ? "s" : ""}
//                         </div>

//                         <button
//                           className="text-blue-600 hover:text-blue-800 text-xs sm:text-sm font-medium"
//                           onClick={() =>
//                             navigate(`/order-details/${order._id}`)
//                           }
//                         >
//                           View
//                         </button>
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               );
//             })}
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default UserOrders;
